<script lang="ts">
  import * as Collapsible from "$lib/components/ui/collapsible/index.js";

  interface Section {
    title: string;
    grade: string;
    description: string;
    summary: string;
    potentialRevenue: string;
    improvements: string[];
  }

  interface FunnelStage {
    name: string;
    description: string;
    sections: Section[];
  }

  const funnelData: FunnelStage[] = [
    {
      name: "TOP OF THE FUNNEL",
      description: "How well you capture the attention of potential customers, answer the phone and distinguish opportunities from non-opportunities",
      sections: [
        {
          title: "Marketing Leads",
          grade: "B-",
          description: "You're doing well at capturing the attention of potential customers, and answering the phone.",
          summary: "You're doing well, but there's room for improvement.",
          potentialRevenue: "Bottom line, in dollars and cents? Achieving a B would have meant as much as $51k in additional revenue this month (that's about $610k over a year).",
          improvements: [
            "Increase inbound calls to 1,824 (currently: 1,603)",
            "Increase unique callers percentage to 74% (currently: 69%)",
            "Reduce abandoned calls to 0 (currently: 92)",
          ],
        },
        {
          title: "Lead Classification",
          grade: "C",
          description: "You're doing so-so at identifying how well the call center distinguishes opportunities from non-opportunities.",
          summary: "This is worth improving.",
          potentialRevenue: "Bottom line, in dollars and cents? Achieving a C+ would have meant as much as $51k in additional revenue this month (that's about $610k over a year).",
          improvements: [
            "Improve call quality scoring",
            "Implement better qualification processes",
            "Conduct team training on opportunity identification",
          ],
        },
      ],
    },
    {
      name: "MIDDLE OF THE FUNNEL",
      description: "How well you convince callers to book an appointment and prepare them for a successful job",
      sections: [
        {
          title: "Booking",
          grade: "A",
          description: "You're doing exceptionally well at converting leads into booked appointments.",
          summary: "Keep up the excellent work!",
          potentialRevenue: "You're maximizing this stage.",
          improvements: [
            "Maintain current booking process excellence",
            "Share best practices with team members",
            "Monitor for consistency",
          ],
        },
      ],
    },
    {
      name: "BOTTOM OF THE FUNNEL",
      description: "How well you dispatch jobs, execute, and convert customers into repeat business",
      sections: [
        {
          title: "Dispatch",
          grade: "B+",
          description: "You're doing well at getting jobs dispatched and completed on time.",
          summary: "You're doing well, but there's room for improvement.",
          potentialRevenue: "Bottom line, in dollars and cents? Achieving a B would have meant as much as $25k in additional revenue this month (that's about $300k over a year).",
          improvements: [
            "Increase appointments count to 582 (currently: 466)",
            "Increase appointments kept rate to 92% (currently: 89%)",
            "Reduce appointments canceled rate to 8% (currently: 10%)",
          ],
        },
        {
          title: "Conversion",
          grade: "C+",
          description: "You're doing so-so at turning completed appointments into sold jobs.",
          summary: "This is worth improving.",
          potentialRevenue: "Bottom line, in dollars and cents? Achieving a B would have meant as much as $75k in additional revenue this month (that's about $900k over a year).",
          improvements: [
            "Increase sold jobs rate to 68% (currently: 61%)",
            "Improve average ticket value to $320 (currently: $290)",
          ],
        },
      ],
    },
  ];

  // Open state per section — pre-initialised so bind:open never receives undefined
  let openSections = $state<Record<string, boolean>>(
    Object.fromEntries(
      funnelData.flatMap(stage =>
        stage.sections.map(section => [sectionKey(stage.name, section.title), false])
      )
    )
  );

  function sectionKey(stageName: string, sectionTitle: string): string {
    return `${stageName}::${sectionTitle}`;
  }

  function getGradeClass(grade: string, size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'lg'): string {
    const letter = grade.charAt(0).toLowerCase();
    const known = ['a', 'b', 'c', 'd', 'f'].includes(letter) ? letter : 'unknown';
    return `grade-${size} grade-${known}-${size}`;
  }

  function gradeLetter(grade: string): string {
    return grade.charAt(0).toUpperCase();
  }

  function gradeModifier(grade: string): string {
    return grade.slice(1).replace('-', '−');
  }
</script>

<div class="flex justify-center items-center min-h-screen bg-background p-4">
  <article class="w-full max-w-3xl bg-white rounded-lg shadow-md p-8">
    <header class="text-center mb-8">
      <h1 class="text-2xl font-bold text-gray-900 mb-1">The Perfect Temp HVAC, Inc.</h1>
      <h2 class="text-lg font-semibold text-gray-500 mb-1">Funnel Report Card</h2>
      <p class="text-xs text-gray-500 mb-6">{new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

      <p class="text-sm font-medium leading-relaxed text-gray-500">
        The sales funnel represents the stages a potential customer goes through before and including making a purchase decision. We use the data from your ServiceTitan account and break it into three stages: the Top, Middle, and Bottom of your funnel. Each section within a stage earns its own score using our AI-driven algorithm, which assigns a grade to the real impact on your results.
      </p>
    </header>

    <div class="flex flex-col gap-6">
      {#each funnelData as stage (stage.name)}
        <div class="group-border">
          <div class="px-6 py-1 bg-gray-100 border-b border-gray-200">
            <h3 class="text-lg font-bold text-gray-800">{stage.name}</h3>
            <p class="text-xs font-medium text-gray-600 py-1 leading-snug -mt-1">{stage.description}</p>
          </div>

          {#each stage.sections as section (section.title)}
            {@const key = sectionKey(stage.name, section.title)}
            <Collapsible.Root bind:open={openSections[key]}>
              <div class="p-4 w-full">
                <div class="w-full flex flex-row items-start gap-4">
                  <div class={getGradeClass(section.grade, 'lg')}>
                    <span class="leading-none">{gradeLetter(section.grade)}</span>
                    {#if gradeModifier(section.grade)}
                      <span class="text-[0.85em] self-start leading-none mt-[0.25em] ml-[0.08em]">{gradeModifier(section.grade)}</span>
                    {/if}
                  </div>

                  <div class="flex-1">
                    <h4 class="text-lg font-semibold text-gray-900">{section.title}</h4>
                    <p class="text-sm font-medium text-gray-600 pb-1">{section.description}</p>
                    <p class="text-sm font-medium text-gray-600 pb-1">{section.summary}</p>
                    <p class="text-sm font-medium text-gray-600 pb-1">{section.potentialRevenue}</p>
                  </div>
                </div>

                <div class="flex justify-end mt-2">
                  <Collapsible.Trigger
                    class="flex flex-row shrink-0 items-center justify-between gap-2 w-40 px-2 py-1 text-[0.7rem] font-semibold text-gray-700 outline outline-1 outline-gray-200 rounded-md cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <span>{openSections[key] ? 'Hide' : 'Improve your grade?'}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" height="10" width="10" viewBox="0 0 320 512" class="shrink-0 transition-transform {openSections[key] ? 'rotate-[270deg]' : 'rotate-90'}">
                      <path fill="#8c1aff" d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"/>
                    </svg>
                  </Collapsible.Trigger>
                </div>
              </div>

              <Collapsible.Content>
                <div class="ml-14 mr-4 mb-4 rounded-md bg-gray-100 px-4 py-2">
                  <h4 class="text-sm font-semibold text-gray-800">How to improve your grade?</h4>
                  <div class="py-2 grid gap-2">
                    <p class="text-xs font-medium text-gray-600">You could improve your grade by working on one or more of these areas:</p>
                    <ul class="list-disc flex flex-col px-4 gap-2">
                      {#each section.improvements as improvement (improvement)}
                        <li class="text-xs font-medium text-gray-600">{improvement}</li>
                      {/each}
                    </ul>
                  </div>
                </div>
              </Collapsible.Content>
            </Collapsible.Root>
          {/each}
        </div>
      {/each}
    </div>
  </article>
</div>

